import { DateTime } from "luxon";

import { ajaxController } from "./controller/AjaxController";
import { clientMutationOptions } from "./controller/clientMutation";
import { PagedResult } from "../DTOs/Common/PagedResult";
import { FlatGroupOption } from "../DTOs/Common/FlatGroupOption";
import {
  DeliveredDownTheHoleDTO,
  ExplosiveMaterialOptionDTO,
  ExplosivePostResultDTO,
  ExplosiveTransactionDTO,
  MagazineStockDTO,
  PendingStocktakeDTO,
  PostExplosiveTransactionRequest,
  ReconciliationLineDTO,
  StocktakeSheetLineDTO,
  StocktakeSubmittedDTO,
  SubmitStocktakeRequest
} from "../DTOs/DrillBlast/ExplosivesDTO";

class ExplosivesAjax {
  private _ajaxController = ajaxController;
  private _endpoint = "/Explosives";

  public getStock() {
    return this._ajaxController.get<PagedResult<MagazineStockDTO>>(`${this._endpoint}/stock`, {});
  }

  public getTransactions(from: DateTime, to: DateTime, magazineId?: number | null) {
    return this._ajaxController.get<PagedResult<ExplosiveTransactionDTO>>(`${this._endpoint}/transactions`, {
      from: from.toISODate(),
      to: to.toISODate(),
      ...(magazineId ? { magazineId } : {}),
    });
  }

  public getMaterials() {
    return this._ajaxController.get<PagedResult<ExplosiveMaterialOptionDTO>>(`${this._endpoint}/materials`, {});
  }

  public getOpenBlasts() {
    return this._ajaxController.get<PagedResult<FlatGroupOption>>(`${this._endpoint}/blasts`, {});
  }

  public post(request: PostExplosiveTransactionRequest) {
    return this._ajaxController.post<PostExplosiveTransactionRequest, ExplosivePostResultDTO>(
      `${this._endpoint}/transactions`, request, clientMutationOptions(request.clientMutationId ?? undefined));
  }

  public reverse(transactionId: number, reason: string) {
    return this._ajaxController.post<{ reason: string }, ExplosiveTransactionDTO>(`${this._endpoint}/transactions/${transactionId}/reverse`, { reason });
  }

  public getParked() {
    return this._ajaxController.get<PagedResult<ExplosiveTransactionDTO>>(`${this._endpoint}/parked`, {});
  }

  public postParked(transactionId: number, note: string | null, acknowledge: boolean) {
    return this._ajaxController.post<{ note: string | null; acknowledge: boolean }, ExplosivePostResultDTO>(
      `${this._endpoint}/parked/${transactionId}/post`, { note, acknowledge });
  }

  public discardParked(transactionId: number, note: string) {
    return this._ajaxController.post<{ note: string }, ExplosiveTransactionDTO>(`${this._endpoint}/parked/${transactionId}/discard`, { note });
  }

  public getStocktakeSheet(magazineId: number) {
    return this._ajaxController.get<PagedResult<StocktakeSheetLineDTO>>(`${this._endpoint}/stocktakes/sheet/${magazineId}`, {});
  }

  public submitStocktake(request: SubmitStocktakeRequest) {
    return this._ajaxController.post<SubmitStocktakeRequest, StocktakeSubmittedDTO>(`${this._endpoint}/stocktakes`, request);
  }

  public getPendingStocktakes() {
    return this._ajaxController.get<PagedResult<PendingStocktakeDTO>>(`${this._endpoint}/stocktakes/pending`, {});
  }

  public approveStocktake(stocktakeId: number, comment: string, rowVersion?: string | null) {
    return this._ajaxController.post<{ comment: string; rowVersion?: string | null }, boolean>(`${this._endpoint}/stocktakes/${stocktakeId}/approve`, { comment, rowVersion });
  }

  public cancelStocktake(stocktakeId: number, comment: string, rowVersion?: string | null) {
    return this._ajaxController.post<{ comment: string; rowVersion?: string | null }, boolean>(`${this._endpoint}/stocktakes/${stocktakeId}/cancel`, { comment, rowVersion });
  }

  public getReconciliation(patternBlastId: number) {
    return this._ajaxController.get<PagedResult<ReconciliationLineDTO>>(`${this._endpoint}/reconciliation/${patternBlastId}`, {});
  }

  public signOffVariance(patternBlastId: number, blastMaterialId: number, reason: string) {
    return this._ajaxController.post<{ blastMaterialId: number; reason: string }, ReconciliationLineDTO[]>(
      `${this._endpoint}/reconciliation/${patternBlastId}/sign-off`, { blastMaterialId, reason });
  }

  public getRegisterHtml(magazineId: number, date: DateTime) {
    return this._ajaxController.get<string>(`${this._endpoint}/register/${magazineId}`, { date: date.toISODate() });
  }

  public getSlip(patternBlastId: number) {
    return this._ajaxController.get<string>(`${this._endpoint}/slip/${patternBlastId}`, {});
  }

  public getDelivered(from: DateTime, to: DateTime) {
    return this._ajaxController.get<PagedResult<DeliveredDownTheHoleDTO>>(`${this._endpoint}/delivered`, { from: from.toISODate(), to: to.toISODate() });
  }
}

export const explosivesAjax = new ExplosivesAjax();
